import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// OTP verification component
import { useState, useRef, useEffect } from "react";
import { useOTPForm } from "../hooks/useAuthForm";
/**
 * OTP verification component with 6-digit input
 */
export function OTPVerification({ phoneNumber, onSuccess, onError, onBack, className }) {
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const inputRefs = useRef([]);
    const { handleSubmit, isLoading, error } = useOTPForm(phoneNumber);
    // Focus first input on mount
    useEffect(() => {
        inputRefs.current[0]?.focus();
    }, []);
    const handleInputChange = (index, value) => {
        // Only allow digits
        if (!/^\d*$/.test(value))
            return;
        const newOtp = [...otp];
        newOtp[index] = value.slice(-1); // Only take the last character
        setOtp(newOtp);
        // Auto-focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };
    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            // Focus previous input on backspace if current is empty
            inputRefs.current[index - 1]?.focus();
        }
    };
    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
        if (pastedData.length === 6) {
            const newOtp = pastedData.split("");
            setOtp(newOtp);
            inputRefs.current[5]?.focus();
        }
    };
    const onSubmit = async (e) => {
        e.preventDefault();
        const code = otp.join("");
        if (code.length !== 6)
            return;
        const result = await handleSubmit({ code });
        if (result.success) {
            onSuccess?.(result.user);
        }
        else {
            onError?.(result.error);
        }
    };
    const isComplete = otp.every((digit) => digit !== "");
    return (_jsxs("div", { className: className, children: [_jsxs("div", { className: "mb-6 text-center", children: [_jsx("h2", { className: "text-textPrimary text-2xl font-bold", children: "Verify Your Phone" }), _jsxs("p", { className: "text-textSecondary mt-2 text-sm", children: ["We sent a 6-digit code to ", phoneNumber] })] }), _jsxs("form", { onSubmit: onSubmit, className: "space-y-8", children: [_jsx("div", { className: "flex justify-center space-x-2 sm:space-x-4", children: otp.map((digit, index) => (_jsx("input", { ref: (el) => (inputRefs.current[index] = el), type: "text", inputMode: "numeric", maxLength: 1, value: digit, onChange: (e) => handleInputChange(index, e.target.value), onKeyDown: (e) => handleKeyDown(index, e), onPaste: handlePaste, disabled: isLoading, className: "border-border bg-surface focus:border-accent focus:ring-accent h-8 w-8 rounded-md border text-center text-xl font-semibold focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-gray-100 sm:h-12 sm:w-12" }, index))) }), error && (_jsx("div", { className: "text-center text-sm text-red-600", role: "alert", children: error })), _jsxs("div", { className: "space-y-4", children: [_jsx("button", { type: "submit", disabled: isLoading || !isComplete, className: "bg-textPrimary focus:ring-accent flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-400", children: isLoading ? "Verifying..." : "Verify OTP" }), onBack && (_jsx("button", { type: "button", onClick: onBack, disabled: isLoading, className: "border-border bg-surface text-textPrimary focus:ring-accent flex w-full justify-center rounded-md border px-4 py-2 text-sm font-medium shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed", children: "Back to Phone Number" }))] })] })] }));
}
